import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { LoadingService } from '../loading.service';
import { GlobalsService } from '../globals.service';
import { PNRLookupAWSInt } from '@app/interfaces/reservation-aws.interface';
import { catchError, lastValueFrom, map, of, retry, throwError, timer } from 'rxjs';
import { PnrAWSMapper } from '@app/mappers/pnr-aws.mapper';
import { AdminService } from '../admin.service';
import { Reservation } from '@app/models/micro/reservation.model';
import moment from 'moment';
import { PassengerListStandbyInt } from '@app/interfaces/micro/passengerListStandby.interface';
import { ElegibilityRespInt } from '@app/interfaces/micro/passengerElegibility.interface';
import { ApiMapper } from '@app/mappers/micro/api.mapper';
import {
  ContactabilityDigitalInt,
  ContactabilityTokenInt,
} from '@app/interfaces/micro/contactability.interface';
import { CheckinPecTabInt } from '@app/interfaces/micro/checkinPecTab.interface';
import { NEW_RELIC_EVENT, TypeLoading } from '@app/enums';
import { AncillariesMapper } from '@app/mappers/micro/acillaries.mapper';
import { DocumentsMapper } from '@app/mappers/micro/documents.mapper';
import { ErrorAdapter } from '@models/adapter/error.adapter';
import { ErrorModel } from '@models/micro/error.model';
import { RespDoCheckIn } from '@models/micro/response/respDoCheckIn.model';
import { RETRY_CONFIG } from '@app/config/retry.config';
import { PnrActivityRetryableError } from '@models/micro/pnr-activity-retryable.error';
import { ArCraftBenefitsModel } from '@app/models/aircraft-benefits.model';
import { AircraftBenefit } from '@app/interfaces/aircraft-benefits.interface';
import { Observable } from 'rxjs';
import { getPnrType, sanitizePnrInput } from '@utils/index';
import { PassengerIdMapService } from '../passenger-id-map.service';
import { NewRelicEventsService } from '../new-relic-events.service';
import { SearchOriginService } from '../search-origin.service';
import { FlightType } from '@app/enums/flight-type.enum';
import {
  FailedEligibilityCacheService,
  FailedEligibilityKeyArgs,
} from './failed-eligibility-cache.service';
import {
  FailedPnrLookupCacheService,
  FailedLookupKeyArgs,
} from './failed-pnr-lookup-cache.service';

type SearchCriterial = 'PNR' | 'AIRPORT' | 'CITY' | 'NUMBERFLIGHT' | 'SCANPASSPORT';

/**
 * Servicio para realizar llamadas a la API de check-in,
 * getViewDocuments,
 * UpdateDocuments,
 * eligibility,
 * passengerlistupgrade,
 * passengerliststandby,
 * digital,
 * getFlightDetails,
 * getBoardingPass,
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  // MyTrips: la respuesta llega cifrada y la descifra ApiEncryptionInterceptor (ver encrypted-services.config).
  urlMicroPNR = `${environment.urlApiMyTrips}${environment.versionMyTrips}/kiosk/mytrips/reservation`;
  urlMiroSeamless: string = `${environment.urlApiCheckin}/tc/reservation${environment.versionSeamless}/kiosk/pnr`;
  urlApiMicroEligibility: string = `${environment.urlApiMicroEligibility}`;
  urlApiMicro: string = `${environment.urlApiMicro}`;
  urlApiContactabilityAuth: string = `${environment.urlApiContactabilityAuth}`;
  urlApiContactability: string = `${environment.urlApiContactability}`;
  pathcms = `${environment.cmsVPro}`;
  constructor(
    private _loadingService: LoadingService,
    private _http: HttpClient,
    private _globalService: GlobalsService,
    private _admin: AdminService,
    private passengerIdMapService: PassengerIdMapService,
    private _newRelicEventsService: NewRelicEventsService,
    private _searchOriginService: SearchOriginService,
    private _failedPnrLookupCache: FailedPnrLookupCacheService,
    private _failedEligibilityCache: FailedEligibilityCacheService
  ) {}

  private buildHttpParams(data: Record<string, unknown>): HttpParams {
    const cleanEntries = Object.entries(data).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    );
    return new HttpParams({
      fromObject: Object.fromEntries(cleanEntries) as Record<string, string>,
    });
  }

  /**
   * Obtiene la reserva de un pasajero basándose en diferentes criterios de búsqueda.
   * @param {SearchCriterial} criteria - Criterio de búsqueda (PNR, Aeropuerto, etc.)
   * @param {string} [firstName] - Nombre del pasajero
   * @param {string} [lastName] - Apellido del pasajero
   * @param {string} [numberFlight] - Número de vuelo
   * @param {string} [arrivalCity] - Ciudad de llegada
   * @param {string} [arrivalAirport] - Aeropuerto de llegada
   * @param {string} [pnr] - Código de reserva (PNR)
   * @returns {Observable<PNRLookupAWSInt>} Datos de la reserva procesados por el mapeador
   */

  getReservation(
    criteria: SearchCriterial,
    firstName: string = null,
    lastName: string = null,
    numberFlight: string = null,
    arrivalCity: string = null,
    arrivalAirport: string = null,
    pnr: string = null
  ): Observable<Reservation[]> {
    const { Location } = this._admin.admin;
    const headers = this.buildPnrLookupHeaders({
      criteria,
      firstName,
      lastName,
      numberFlight,
      arrivalCity,
      arrivalAirport,
      pnr,
    });

    // Clave de la búsqueda para el cache de fallos de sesión. Cubre tanto la
    // búsqueda por PNR/TICKET como la búsqueda por destino + apellido.
    const lookupKey: FailedLookupKeyArgs = {
      criteria,
      pnr,
      firstName,
      lastName,
      numberFlight,
      arrivalCity,
      arrivalAirport,
      departureAirport: Location,
    };

    // Si esta misma búsqueda ya falló por una razón de negocio en esta sesión,
    // devolvemos el mismo error sin llamar al backend ni registrar isError en
    // New Relic. Solo se relanza al backend cuando el pasajero cambia el dato
    // (otra clave) o al reiniciar la sesión (cache vacío).
    const cachedError = this._failedPnrLookupCache.get(lookupKey);
    if (cachedError) {
      return throwError(cachedError);
    }

    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http.get<PNRLookupAWSInt>(this.urlMicroPNR, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        this._globalService.setPNRLookupAWSInt(res);

        const reservation = PnrAWSMapper.pnrBuild(res, Location);
        let legEntity: FlightType | undefined;
        const flightSummary = reservation[0]?.flightSummary;

        if (flightSummary?.isDomestic === true) {
          legEntity = FlightType.DOMESTIC;
        } else if (flightSummary?.isDomestic === false) {
          legEntity = FlightType.INTERNATIONAL;
        } else {
          legEntity = undefined;
        }

        const windowTypeTimeDifference =
          (Date.now() - new Date(flightSummary?.departureDateTime).getTime()) / (1000 * 60 * 60);

        this._newRelicEventsService.trackCheckInEvent(NEW_RELIC_EVENT.GET_RESERVATION, {
          legEntity: legEntity,
          origin: flightSummary?.departureAirport,
          destination: flightSummary?.arrivalAirport,
          branded_fare: flightSummary?.fareFamily,
          transactionId: flightSummary?.cartId,
          windowType: Math.abs(windowTypeTimeDifference) > 72 ? 'comercial' : 'operacional',
          searchOrigin: this._searchOriginService.consumeOrigin() ?? 'unknown',
        });
        return PnrAWSMapper.pnrBuild(res, Location);
      }),
      catchError((error) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        const adapter = ErrorAdapter.adapt(error?.error || error);
        // Solo persiste en cache si es una razón de negocio (NO_RESERVATIONS,
        // PNR_NOT_FOUND, MANY_RESERVATIONS, NO_ITINERARY). Errores técnicos o
        // transitorios no se cachean para permitir reintentos legítimos.
        this._failedPnrLookupCache.set(lookupKey, adapter);
        return throwError(adapter);
      })
    );
  }

  private buildPnrLookupHeaders(args: {
    criteria: SearchCriterial;
    firstName: string;
    lastName: string;
    numberFlight: string;
    arrivalCity: string;
    arrivalAirport: string;
    pnr: string;
  }): HttpHeaders {
    const { Location } = this._admin.admin;
    const sanitized = sanitizePnrInput(args.pnr);
    const kind = getPnrType(sanitized);

    // CVT20-13847: Si no viene lastName, tomar del primer pasajero en memoria
    let lastName = args.lastName || '';
    if (!lastName) {
      try {
        const sessionReservation = sessionStorage.getItem('reservation');
        if (sessionReservation) {
          const parsed = JSON.parse(sessionReservation);
          if (Array.isArray(parsed?.passengers) && parsed.passengers.length > 0) {
            lastName = parsed.passengers[0]?.lastName || '';
          }
        }
      } catch (e) {
        // intentional: silently ignore sessionStorage parse errors
      }
    }

    const base: Record<string, string> = {
      channel: 'KIOSK',
      'x-transactionId': '',
      searchCriteria: args.criteria,
      firstName: args.firstName || '',
      lastName,
      numberFlight: args.numberFlight || '',
      arrivalCity: args.arrivalCity || '',
      arrivalAirport: args.arrivalAirport || '',
      dateTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
      departureAirport: Location || '',
      // La versión del BFF ya no va en la URL; viaja en este header con formato 'v1'.
      versionBFF: (environment.versionSeamless || '').replace(/^\//, ''),
    };

    if (kind === 'TICKET') {
      base.ticket = sanitized;
    } else {
      base.pnr = sanitized;
    }

    return new HttpHeaders(base);
  }

  /**
   * Obtiene la elegibilidad de un pasajero para el proceso de check-in.
   *
   * @param {PNRLookupAWSInt} reservation - Información de la reservación obtenida desde el sistema AWS.
   * @param {TypeLoading} typeLoading - Tipo de indicador de carga que se mostrará mientras se procesa la solicitud.
   * @param {Reservation} lookup - Información detallada de la reservación en el sistema interno.
   * @returns {Observable<ElegibilityRespInt>} - Un observable que emite la respuesta de elegibilidad del check-in.
   */

  getEligibility(reservation: PNRLookupAWSInt, typeLoading: TypeLoading, lookup: Reservation) {
    const body = ApiMapper.requestEligibility(reservation, lookup);

    // Clave del cache de eligibility fallida por sesión: PNR + leg (itinerario).
    const eligibilityKey: FailedEligibilityKeyArgs = {
      reservationCode: reservation?.reservationCode,
      legCode: lookup?.flightSummary?.legCode,
    };

    // Si esta misma evaluación ya falló por una razón determinista de negocio
    // (hoy: BAD_REQUEST por arrivalCountry vacío en segmentos SkyTeam) en esta
    // sesión, devolvemos el mismo error sin llamar al backend ni registrar otro
    // isError en New Relic. Solo se relanza si cambia el PNR/leg (otra clave) o
    // al reiniciar la sesión (cache vacío). Los errores técnicos (500,
    // CONNECTION_REFUSED, timeout) NO se cachean: deben poder reintentarse.
    const cachedError = this._failedEligibilityCache.get(eligibilityKey);
    if (cachedError) {
      // No se llama hideLoading aquí: el early-return ocurre ANTES del
      // showLoading de esta petición, así que no hay loading propio que ocultar
      // (igual que la rama de cache del FailedPnrLookup). Llamarlo decrementaría
      // un contador no incrementado y podría ocultar un loading concurrente.
      return throwError(cachedError);
    }

    let headers = new HttpHeaders({
      channel: 'KIOSK',
      flow: 'checkin',
      cacheEnabled: 'false',
      'x-transaction-id': reservation.transactionId,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    });
    this._loadingService.showLoading(typeLoading);
    return this._http
      .post<ElegibilityRespInt>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin${environment.versionEligibility}/eligibility`,
        body,
        { headers }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(typeLoading);

          return res as ElegibilityRespInt;
        }),
        catchError((error) => {
          this._loadingService.hideLoading(typeLoading);
          const adapter = ErrorAdapter.adapt(error?.error || error);
          // Solo persiste si es la razón determinista de negocio; los técnicos
          // no se cachean para permitir reintentos legítimos.
          this._failedEligibilityCache.set(eligibilityKey, adapter);
          return throwError(adapter);
        })
      );
  }

  /**
   * Obtiene la lista de pasajeros en espera.
   *
   * @param {Reservation} reservation - Información de la reservación del vuelo.
   * @returns {Observable<PassengerListStandbyInt>} - Un observable que emite la lista de pasajeros en espera para una mejora de clase.
   */

  getPassengerListUpgrade(reservation: Reservation) {
    const data = ApiMapper.mapPassengerListUpgrade(reservation);
    data.departureDate = this.formatDate(data.departureDate);
    const params = this.buildHttpParams({ ...data } as Record<string, unknown>);
    let headers = new HttpHeaders({
      channel: 'KIOSK',
      flow: 'checkin',
      'x-transaction-id': reservation.flightSummary.cartId,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http
      .get<PassengerListStandbyInt>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/passengerListUpgrade${environment.versionPassengerList}`,
        {
          headers,
          params,
        }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  formatDate(date) {
    const response = moment(date).format('YYYY-MM-DD');
    return response;
  }

  /**
   * Obtiene los documentos requeridos para el proceso de check-in de una reservación.
   *
   * @param {Reservation} reservation - Información de la reservación del vuelo.
   * @returns {Observable<any>} - Un observable que emite la información de los documentos requeridos.
   */

  getRequireDocuments(reservation: Reservation) {
    const body = ApiMapper.requestRequireDocuments(reservation);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http
      .post(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/checkInDocuments/getViewDocuments`,
        body,
        {
          headers: {
            channel: 'KIOSK',
            flow: 'checkin',
            cacheEnabled: 'false',
            'x-transaction-id': reservation.flightSummary.cartId,
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
        }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return DocumentsMapper.responseUpdateDocuments(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  /**
   * Obtiene la lista de pasajeros en lista de espera (standby) para un vuelo específico.
   *
   * @param {Reservation} reservation - Información de la reservación del vuelo.
   * @returns {Observable<PassengerListStandbyInt>} - Un observable que emite la lista de pasajeros en espera.
   */

  getPassengerListstandby(reservation: Reservation) {
    const data = ApiMapper.requestListstandby(reservation);
    const params = new HttpParams({ fromObject: { ...data } });
    let headers = new HttpHeaders({
      channel: 'KIOSK',
      flow: 'checkin',
      'x-transaction-id': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
    });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http
      .get<PassengerListStandbyInt>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/passengerlist${environment.versionPassengerList}/standby`,
        {
          headers,
          params,
        }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  /**
   * Obtiene un token de autenticación para el servicio de contactabilidad.
   *
   * @returns {Promise<ContactabilityTokenInt>} - Una promesa que resuelve con el token de autenticación.
   */

  async contactabilityToken() {
    const data = {
      apiName: environment.apiNameContactability,
      // Resolucion de vulnerabilidades Bolivia - junio 2026: CWE-798 - Secret inyectado por CI/CD via environment
      secret: environment.contactabilityAuth,
    };
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    const token$ = this._http
      .post<ContactabilityTokenInt>(`${this.urlApiContactabilityAuth}/auth`, data)
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
    return await lastValueFrom<ContactabilityTokenInt>(token$);
  }

  /**
   * Envía una solicitud al servicio de contactabilidad digital para una reserva específica.
   *
   * @param {Reservation} reservation - Objeto de reserva que contiene los datos necesarios para la solicitud.
   * @returns {Promise<ContactabilityDigitalInt>} - Una promesa que resuelve con la respuesta del servicio de contactabilidad digital.
   */

  async contactabilityDigital(reservation: Reservation): Promise<ContactabilityDigitalInt> {
    const token = await this.contactabilityToken();
    const body = ApiMapper.mapContactabilityDigital(reservation);
    let headers = new HttpHeaders({
      Authorization: `Bearer ${token.message}`,
    });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return new Promise((resolve, reject) => {
      this._http
        .post<ContactabilityDigitalInt>(`${this.urlApiContactability}/digital`, body, { headers })
        .subscribe({
          next: (response) => {
            return resolve(response);
          },
          error: (error) => {
            this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
            return reject(error);
          },
          complete: () => {
            this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          },
        });
    });
  }

  /**
   * Obtiene los detalles del vuelo para una reserva específica.
   *
   * @param {Reservation} reservation - Objeto de reserva que contiene la información necesaria para la solicitud.
   * @returns {Observable<any>} - Un observable que emite la respuesta con los detalles del vuelo.
   */

  getFlightDetails(reservation: Reservation) {
    const body = ApiMapper.requestFlightDetails(reservation);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http
      .post(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/flightDetails`,
        body,
        {
          headers: {
            channel: 'KIOSK',
            flow: 'checkin',
            cacheEnabled: 'false',
            'x-transaction-id': reservation.flightSummary.cartId,
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
        }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  /**
   * Manejo de errores HTTP para las peticiones.
   * @param {HttpErrorResponse} error - Respuesta de error HTTP
   * @returns {Observable<never>} Observable con error procesado
   */

  handlerError(
    error: HttpErrorResponse,
    TypeLoadingShow: TypeLoading = TypeLoading.FOOTER_LOADING
  ) {
    this._loadingService.hideLoading(TypeLoadingShow);

    const adapter = ErrorAdapter.adapt(error?.error || error);
    return throwError(adapter);
  }

  /**
   * Solicita la reimpresión de una tarjeta de  (boarding pass) para una reserva.
   *
   * Esta función toma un objeto de tipo `reservation`, genera el cuerpo de la solicitud
   * usando `ApiMapper`, y realiza una solicitud POST a un servicio externo para obtener
   * la tarjeta de embarque. Durante el proceso, muestra un indicador de carga y maneja
   * cualquier error que pueda ocurrir en la solicitud HTTP.
   *
   * @param {Reservation} reservation - La reserva para la cual se solicita la reimpresión de la tarjeta de embarque.
   *
   * @returns {Observable<any>} Un observable que emite la respuesta del servicio con la tarjeta de embarque.
   * En caso de error, se maneja mediante un interceptor de errores.
   */

  getReprintBP(reservation) {
    const body = ApiMapper.requestBoardingPass(reservation);

    let headers = new HttpHeaders({
      channel: 'KIOSK',
      flow: 'checkin',
      'x-transaction-id': reservation.flightSummary.cartId,
    });

    this._loadingService.showLoading(TypeLoading.COMPONENT_LOADING);

    return this._http
      .post<Record<string, unknown>>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/getBoardingPass`,
        body,
        { headers }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.COMPONENT_LOADING);
          return ApiMapper.respBoardingPass(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.COMPONENT_LOADING))
      );
  }
  /**
   * Realiza un proceso de check-in para una reserva.
   *
   * Esta función toma un objeto de tipo `Reservation`, genera el cuerpo de la solicitud
   * usando un `ApiMapper`, y realiza una solicitud POST a un servicio externo para
   * realizar el check-in de la reserva. Durante el proceso, muestra un indicador de carga
   * y maneja los errores que puedan ocurrir en la solicitud HTTP.
   *
   * @param {Reservation} reservation - La reserva para la cual se va a realizar el check-in.
   *
   * @returns {Observable<CheckinPecTabInt>} Un observable que emite la respuesta del check-in
   * tras recibir la respuesta del servidor. En caso de error, se maneja mediante un
   * interceptor de errores.
   */
  doCheckIn(reservation: Reservation, reprintBp: boolean) {
    const body = ApiMapper.requestPecTab(reservation, reprintBp, this.passengerIdMapService);
    let headers = new HttpHeaders({
      channel: 'KIOSK',
      flow: 'checkin',
      cacheEnabled: 'false',
      'x-transaction-id': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);

    return this._http
      .post<CheckinPecTabInt>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/docheckin`,
        body,
        { headers }
      )
      .pipe(
        // Mapea al modelo tipado y, si trae warning 3060, lo convierte en error
        // sintetico para que retry() trate igual el vector "warning en 200" y
        // el vector "error HTTP real" — mismo camino, un solo retry().
        map((res) => {
          const mapped = ApiMapper.respDoCheckIn(res);
          if (this.hasPnrActivityWarning(mapped)) {
            throw new PnrActivityRetryableError(mapped);
          }
          return mapped;
        }),
        retry({
          count: RETRY_CONFIG.PNR_ACTIVITY.maxRetries,
          delay: (error, retryCount) => {
            const adapted =
              error instanceof PnrActivityRetryableError
                ? error
                : ErrorAdapter.adapt(error?.error || error);

            if (this.isPnrActivityError(adapted)) {
              this.logPnrActivityRetry(retryCount, reservation, adapted);
              return timer(RETRY_CONFIG.PNR_ACTIVITY.delayMs);
            }
            return throwError(() => error); // no es 3060: no reintentar
          },
        }),
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => {
          if (error instanceof PnrActivityRetryableError) {
            // Reintentos agotados en el vector "warning": se devuelve la
            // respuesta original (con el warning intacto) por el canal de
            // exito para que getWarning() en ImportantInfoComponent dispare el
            // modal correcto (errorMsj.msj_3060) sin cambios en el componente.
            this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
            return of(error.response);
          }
          return this.handlerError(error, TypeLoading.FOOTER_LOADING);
        })
      );
  }

  /**
   * Determina si la respuesta mapeada contiene el warning 3060 (PNR ACTIVITY
   * IN PROGRESS). Alineado con ImportantInfoComponent.getWarning(): detecta por
   * codigo o por mensaje de warning.
   */
  private hasPnrActivityWarning(response: RespDoCheckIn): boolean {
    return (response?.warnings || []).some(
      (warning) =>
        warning?.code === RETRY_CONFIG.PNR_ACTIVITY.errorCode ||
        warning?.errorMessage === RETRY_CONFIG.PNR_ACTIVITY.errorMessage
    );
  }

  /**
   * Identifica si un error corresponde al vector 3060, ya sea el warning
   * sintetico (respuesta 200) o el error HTTP real adaptado a ErrorModel.
   */
  private isPnrActivityError(error: ErrorModel | PnrActivityRetryableError): boolean {
    if (error instanceof PnrActivityRetryableError) {
      return true;
    }
    return error instanceof ErrorModel && error.getCode() === RETRY_CONFIG.PNR_ACTIVITY.errorCode;
  }

  /**
   * Registra en New Relic cada reintento del error 3060 para monitoreo.
   * Usa el objeto global newrelic (precedente: global-error-handler.ts).
   */
  private logPnrActivityRetry(
    attempt: number,
    reservation: Reservation,
    error: ErrorModel | PnrActivityRetryableError
  ): void {
    const newRelic = (window as any).newrelic;
    newRelic?.addPageAction?.('PNR_ACTIVITY_RETRY', {
      attempt,
      pnr: reservation?.pnr || 'unknown',
      cartId: reservation?.flightSummary?.cartId || 'unknown',
      vector: error instanceof PnrActivityRetryableError ? 'response_warning' : 'http_error',
      timestamp: new Date().toISOString(),
    });
  }

  getAircraftBenefits(language: string): Observable<ArCraftBenefitsModel[]> {
    const lang = language && language.toLowerCase() === 'es' ? 'es' : 'en';
    const url = `${this.pathcms}/aircraftapp?language=${lang}`;
    return this._http.get<Record<string, unknown>[]>(url).pipe(
      map((res) =>
        res.map(
          (item) =>
            new ArCraftBenefitsModel(
              item as Partial<AircraftBenefit & { aircraftType: string }>,
              lang
            )
        )
      ),
      catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
    );
  }
}
